import { useMemo, useContext } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Typography, styled, Box } from '@hermes/web-components'
import { getPracticeSpecialistName } from '../../../utils/practiceSpecialist'
import FaqItem from '../FaqItem'
import { transformAddressToString } from '../../../utils/address'
import { AppDataContext } from '../../../providers/AppData'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { PracticeProfile } from '../../../types/practice'
import { useWorkingHoursToStringDetailed } from '../../../utils/workingHours'

const StyledLink = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  textTransform: 'lowercase',
  '&:hover': {
    cursor: 'pointer'
  }
}))

const PracticeFAQ = ({
  practice,
  hideGetInTouch
}: {
  practice: PracticeProfile
  onGetInTouch?: () => void
  hideGetInTouch: boolean
}) => {
  const router = useRouter()
  const [pathName] = router.asPath.split('#')
  const { language } = useContext(AppDataContext)
  const translate = useTranslateMessage()
  const { practiceName, address, specialistName } = useMemo(() => {
    const { name, specialists } = practice
    return {
      practiceName: name[language],
      address: transformAddressToString(practice?.address, language),
      specialistName: getPracticeSpecialistName(specialists?.[0], language)
    }
  }, [practice, language])

  const workingHoursString = useWorkingHoursToStringDetailed(practice.workingOpeningHours)
  const hasWorkingHours = !!practice?.workingOpeningHours?.[1]?.length

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h2" component="h2" textTransform="uppercase" sx={{ mb: 2 }}>
        {translate('faq')}
      </Typography>
      {!!practice.reviewsTotal && (
        <FaqItem
          title={translate('profile.question_reviews', { name: practiceName })}
          mainDescription={translate('profile.answer_reviews', {
            name: practiceName,
            rating: practice.averageRating
          })}
          additionalDescription={
            <>
              {translate('profile.based_on_reviews', {
                name: practiceName,
                rating: practice.averageRating
              })}{' '}
              <Link href={`/${router.locale}${pathName}#reviews`}>
                <StyledLink style={{ textTransform: 'capitalize' }}>
                  {translate('reviews.count', { reviews: practice.reviewsTotal }, { bold: { fontWeight: 'normal' } })}.
                </StyledLink>
              </Link>
            </>
          }
        />
      )}
      {!!specialistName && (
        <FaqItem
          title={translate('profile.question_where_located', {
            name: practiceName
          })}
          mainDescription={translate('profile.practices_located_at', {
            practice: practiceName,
            address
          })}
        />
      )}

      {hasWorkingHours && (
        <FaqItem
          title={translate('profile.questions_practice_location', {
            name: practiceName
          })}
          mainDescription={translate('profile.answer_practice_location', {
            name: practiceName,
            hours: workingHoursString
          })}
        />
      )}
      {!!practice?.specialists?.length && (
        <FaqItem
          title={translate('profile.question_consultants', { name: practiceName })}
          mainDescription={
            <>
              {translate('profile.answer_consultants', { name: practiceName })}{' '}
              <Link href={`/${router.locale}${pathName}#specialists`}>
                <StyledLink>{translate('here')}.</StyledLink>
              </Link>
            </>
          }
        />
      )}
      {!hideGetInTouch && (
        <FaqItem
          title={translate('profile.question_accept_visits', { name: practiceName })}
          mainDescription={translate('profile.answer_accept_visits', { name: practiceName })}
          additionalDescription={translate('profile.short_enquire_as_a_new_patient')}
        />
      )}
    </Box>
  )
}

export default PracticeFAQ
